import { Box, Typography } from '@mui/material';
import { ReactNode } from 'react';
import Icon from '../../IconSvg/Icon';
import LelyCheckbox, { LelyCheckboxProps } from '../LelyCheckbox/LelyCheckbox';
import { LelyIconName } from '../buttonTypes';

export type LelyCheckboxWithDetailsProps = Omit<
  LelyCheckboxProps,
  'children' | 'label'
> & {
  children?: ReactNode;
  count?: ReactNode;
  icon?: LelyIconName;
  iconColor?: string;
  label?: ReactNode;
  swatchColor?: string;
};

const getDefaultCheckboxId = (labelContent: ReactNode) => {
  if (typeof labelContent === 'string' || typeof labelContent === 'number') {
    return String(labelContent);
  }

  return undefined;
};

function LelyCheckboxWithDetails({
  children,
  count,
  id,
  icon,
  iconColor,
  label,
  name,
  swatchColor,
  ...props
}: LelyCheckboxWithDetailsProps) {
  const labelContent = label ?? children;
  const checkboxId = id ?? getDefaultCheckboxId(labelContent);
  const checkboxName = name ?? getDefaultCheckboxId(labelContent);

  if (!labelContent) {
    return <LelyCheckbox {...props} id={id} name={name} />;
  }

  return (
    <LelyCheckbox
      {...props}
      id={checkboxId}
      name={checkboxName}
      label={
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            flexWrap: 'nowrap',
            gap: '4px',
            minWidth: 0,
            width: '100%',
          }}
        >
          {swatchColor && (
            <Box
              sx={{
                backgroundColor: swatchColor,
                borderRadius: '50%',
                flexShrink: 0,
                height: '12px',
                marginRight: 1,
                width: '12px',
              }}
            />
          )}
          {icon && (
            <Box
              sx={{
                alignItems: 'center',
                color: iconColor,
                display: 'flex',
                flexShrink: 0,
                height: '20px',
                marginRight: 1,
                width: '20px',

                '& .icon-svg': {
                  height: '20px',
                  width: '20px',
                },
              }}
            >
              <Icon name={icon} style={{ color: iconColor }} />
            </Box>
          )}
          <Typography
            variant="xs"
            component="span"
            data-testid="checkbox-label"
            sx={(theme) => ({
              display: '-webkit-box',
              flex: 1,
              fontFamily: theme.lelyFontFamily.UBUNTU,
              minWidth: 0,
              overflow: 'hidden',
              overflowWrap: 'anywhere',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 2,
            })}
          >
            {labelContent}
          </Typography>
          {count !== undefined ? (
            <Typography
              variant="xs"
              component="span"
              data-testid="checkbox-count"
              sx={(theme) => ({
                display: 'inline-flex',
                flexShrink: 0,
                fontFamily: theme.lelyFontFamily.UBUNTU.split(',')[0],
                justifyContent: 'center',
                textAlign: 'center',
                width: '24px',
              })}
            >
              {count}
            </Typography>
          ) : null}
        </Box>
      }
    />
  );
}

export default LelyCheckboxWithDetails;
